import React, { useState, useEffect } from 'react';
import { getLiveDashboard, getEmployees } from '../api';

function Home() {
  const [dashboardData, setDashboardData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = user.tenant_id || 1;
  const userName = user.first_name || 'there';

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, empRes] = await Promise.all([
        getLiveDashboard(tenantId),
        getEmployees(tenantId)
      ]);
      setDashboardData(dashRes.data);
      setEmployees(empRes.data.filter(e => e.active));
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTotals = () => {
    return dashboardData.reduce((acc, loc) => {
      return {
        totalCars: acc.totalCars + (parseInt(loc.total_cars) || 0),
        carsOnLot: acc.carsOnLot + (parseInt(loc.cars_on_lot) || 0),
        employees: acc.employees + (parseInt(loc.current_employees) || 0),
        cash: acc.cash + (parseFloat(loc.cash_collected) || 0),
        card: acc.card + (parseFloat(loc.card_collected) || 0),
        venmo: acc.venmo + (parseFloat(loc.venmo_collected) || 0)
      };
    }, { totalCars: 0, carsOnLot: 0, employees: 0, cash: 0, card: 0, venmo: 0 });
  };

  const totals = getTotals();
  const totalRevenue = totals.cash + totals.card + totals.venmo;
  const activeLocationsToday = dashboardData.filter(d => d.shift_id).length;

  const currentHour = new Date().getHours();
  const greeting = currentHour < 12 ? 'Good morning' : currentHour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading) {
    return (
      <div style={{ padding: '48px' }}>
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '48px', paddingBottom: '40px' }}>
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{greeting}, {userName}</h1>
        <p className="text-gray-600 mt-1">Here's what's happening today.</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6" style={{backgroundColor: '#ffffff'}}>
          <p className="text-sm text-gray-500 mb-1">Active Locations</p>
          <p className="text-3xl font-bold text-gray-900">{activeLocationsToday}</p>
          <p className="text-xs text-gray-400 mt-1">of {dashboardData.length} total</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6" style={{backgroundColor: '#ffffff'}}>
          <p className="text-sm text-gray-500 mb-1">Cars Today</p>
          <p className="text-3xl font-bold text-gray-900">{totals.totalCars}</p>
          <p className="text-xs text-gray-400 mt-1">{totals.carsOnLot} currently on lot</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6" style={{backgroundColor: '#ffffff'}}>
          <p className="text-sm text-gray-500 mb-1">Employees Working</p>
          <p className="text-3xl font-bold text-gray-900">{totals.employees}</p>
          <p className="text-xs text-gray-400 mt-1">of {employees.length} total staff</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6" style={{backgroundColor: '#ffffff'}}>
          <p className="text-sm text-gray-500 mb-1">Revenue Today</p>
          <p className="text-3xl font-bold" style={{color: '#16a34a'}}>${totalRevenue.toFixed(2)}</p>
          <p className="text-xs text-gray-400 mt-1">Cash: ${totals.cash.toFixed(2)} | Card: ${totals.card.toFixed(2)}</p>
        </div>
      </div>

      {/* Location Status */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Today's Locations</h2>
        <div className="bg-white rounded-lg shadow overflow-hidden" style={{backgroundColor: '#ffffff'}}>
          <table className="w-full">
            <thead>
              <tr style={{backgroundColor: '#f9fafb'}}>
                <th className="text-left p-4 text-sm font-semibold text-gray-900">Location</th>
                <th className="text-center p-4 text-sm font-semibold text-gray-900">Status</th>
                <th className="text-center p-4 text-sm font-semibold text-gray-900">Cars</th>
                <th className="text-center p-4 text-sm font-semibold text-gray-900">On Lot</th>
                <th className="text-center p-4 text-sm font-semibold text-gray-900">Staff</th>
                <th className="text-right p-4 text-sm font-semibold text-gray-900">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.map((location, index) => {
                const locRevenue = (parseFloat(location.cash_collected) || 0) + 
                                   (parseFloat(location.card_collected) || 0) + 
                                   (parseFloat(location.venmo_collected) || 0);
                return (
                  <tr key={location.location_id} style={{borderTop: '1px solid #e5e7eb'}}>
                    <td className="p-4">
                      <span className="font-medium text-gray-900">{location.location_name}</span>
                    </td>
                    <td className="p-4 text-center">
                      {location.shift_id ? (
                        <span style={{
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          padding: '4px 12px',
                          borderRadius: '9999px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          Active
                        </span>
                      ) : (
                        <span style={{
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280',
                          padding: '4px 12px',
                          borderRadius: '9999px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          No Shift
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-center text-gray-900">{location.total_cars || 0}</td>
                    <td className="p-4 text-center text-gray-900">{location.cars_on_lot || 0}</td>
                    <td className="p-4 text-center text-gray-900">{location.current_employees || 0}</td>
                    <td className="p-4 text-right font-medium" style={{color: '#16a34a'}}>
                      ${locRevenue.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a href="/scheduling" className="bg-white rounded-lg shadow p-4 text-center hover:shadow-md transition-shadow" style={{backgroundColor: '#ffffff', textDecoration: 'none'}}>
            <span style={{fontSize: '24px'}}>📅</span>
            <p className="text-gray-700 mt-2 font-medium">Create Schedule</p>
          </a>
          <a href="/employees" className="bg-white rounded-lg shadow p-4 text-center hover:shadow-md transition-shadow" style={{backgroundColor: '#ffffff', textDecoration: 'none'}}>
            <span style={{fontSize: '24px'}}>👤</span>
            <p className="text-gray-700 mt-2 font-medium">Add Employee</p>
          </a>
          <a href="/events" className="bg-white rounded-lg shadow p-4 text-center hover:shadow-md transition-shadow" style={{backgroundColor: '#ffffff', textDecoration: 'none'}}>
            <span style={{fontSize: '24px'}}>🎉</span>
            <p className="text-gray-700 mt-2 font-medium">New Event</p>
          </a>
          <a href="/live" className="bg-white rounded-lg shadow p-4 text-center hover:shadow-md transition-shadow" style={{backgroundColor: '#ffffff', textDecoration: 'none'}}>
            <span style={{fontSize: '24px'}}>📊</span>
            <p className="text-gray-700 mt-2 font-medium">View Live Stats</p>
          </a>
        </div>
      </div>
    </div>
  );
}

export default Home;